import React, { useRef, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function PageHeader({
  title,
  subtitle,
  icon,
  badge,
  meta = [],
  actions,
  tabs,
  activeKey,
  onTabChange,
  sticky = false,
  className,
  children,
}) {
  const WrapperTag = sticky ? "div" : "div";

  return (
    <WrapperTag
      className={cx(
        "bg-background",
        sticky && "sticky top-14 z-30 bg-background/80 backdrop-blur",
        "border-b border-border",
        className
      )}
    >
      <div className="container">
        {/* Top Row: Title + Actions */}
        <div className="flex items-start justify-between gap-4 py-5">
          <div className="min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              {icon && (
                <div className="shrink-0 text-muted-foreground">{icon}</div>
              )}
              <h1 className="truncate text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
              {typeof badge === "string" ? (
                <span className="badge badge-brand">{badge}</span>
              ) : (
                badge || null
              )}
            </div>

            {subtitle && (
              <p className="mt-2 text-sm text-muted-foreground max-w-prose">
                {subtitle}
              </p>
            )}

            {meta.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {meta.map((m) => (
                  <span
                    key={m.label}
                    className="badge badge-muted"
                    title={m.label}
                  >
                    <span className="text-muted-foreground">{m.label}:</span>
                    <span className="ml-1">{m.value}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {actions && (
            <div className="shrink-0 flex items-center gap-2">{actions}</div>
          )}
        </div>

        {Array.isArray(tabs) && tabs.length > 0 && (
          <TabsRow
            tabs={tabs}
            activeKey={activeKey}
            onTabChange={onTabChange}
          />
        )}

        {children && <div className="py-4">{children}</div>}
      </div>
    </WrapperTag>
  );
}

function TabsRow({ tabs, activeKey, onTabChange }) {
  const [underlineStyle, setUnderlineStyle] = useState(null);
  const tabRefs = useRef({});
  const location = useLocation();

  // Update underline whenever activeKey or route changes
  useEffect(() => {
    let currentKey = activeKey;

    if (!currentKey) {
      // Find the NavLink that is styled as active
      const activeTab = Object.entries(tabRefs.current).find(
        ([, el]) => el && el.classList.contains("text-foreground")
      );
      if (activeTab) currentKey = activeTab[0];
    }

    if (!currentKey) return;
    const el = tabRefs.current[currentKey];
    if (el) {
      setUnderlineStyle({
        left: el.offsetLeft,
        width: el.offsetWidth,
      });
    }
  }, [activeKey, location.pathname, tabs]);

  return (
    <div className="border-t border-border relative">
      <div
        role="tablist"
        aria-label="Page sections"
        className="flex items-center gap-1 overflow-x-auto overflow-y-hidden scrollbar-none relative"
      >
        {tabs.map((t) => {
          const base =
            "relative inline-flex items-center h-11 px-3 text-sm rounded-md transition-colors";
          const idle =
            "text-muted-foreground hover:text-foreground hover:bg-muted/60";
          const activeClasses = "text-foreground";

          if (t.to) {
            return (
              <NavLink
                key={t.key}
                to={t.to}
                ref={(el) => (tabRefs.current[t.key] = el)}
                className={({ isActive }) =>
                  cx(base, isActive ? activeClasses : idle)
                }
                role="tab"
              >
                {t.label}
              </NavLink>
            );
          }

          const isActive = activeKey === t.key;
          return (
            <button
              key={t.key}
              ref={(el) => (tabRefs.current[t.key] = el)}
              type="button"
              onClick={() => onTabChange?.(t.key)}
              className={cx(base, isActive ? activeClasses : idle)}
              role="tab"
              aria-selected={isActive}
            >
              {t.label}
            </button>
          );
        })}

        {/* Single sliding underline */}
        {underlineStyle && (
          <span
            className="absolute bottom-0 h-[2px] bg-brand rounded transition-all duration-300 ease-in-out"
            style={underlineStyle}
          />
        )}
      </div>
    </div>
  );
}
